import React, { createContext, useContext, useState } from 'react';
import { IUserBookingTicket } from '../models/BookingTicket';

export enum Modals {
  None,
  TermsHighlight,
  DeleteAccount,
  DeactivateAccount,
  ReactivateAccount,
  ResetPassword,
  EditGuestDetails,
  RemoveTickets,
  AcceptTicket,
  DeclineTicket,
  NonPrioritySessionSelected,
  PriorityTicketsFullyBooked,
  NotEnoughPriorityTickets,
  SaveProgress,
  AccessibilityRequirementsChange,
  AllocationAccessibilityRequirementsAdded,
  AllocationAccessibilityRequirementsRemoved,
  AllocationUpdatedAge,
  AllocationSendEmailNotifications,
  UnregisteredGuestEmails,
  SetGender
};

type ModalProviderProps = {
  children: React.ReactNode;
};

const ModalContext = createContext<{
  modalState: Modals;
  modalBookingData: ModalBookingData;
  modalTexts: ModalTexts;
  modalOnClick?: (successful?: boolean, message?: React.ReactElement | string) => Promise<void> | void;
  openModal: (name: Modals, onClick?: (successful?: boolean, message?: React.ReactElement | string) => void) => void;
  closeModal: () => void;
  setCurrentBookingData: (modalBookingData: ModalBookingData) => void;
  setCurrentTexts: (modalTexts: ModalTexts) => void;
} | undefined>(undefined);

export interface ModalBookingData {
  bookingInterest: IUserBookingTicket;
  minAge?: number;
  isPriority?: boolean;
}

export interface ModalTexts {
  mainBody: string;
  recipientEmails?: string[];
  /**
   * Set by the booking form so the unregistered-guest warning can say which of the two things
   * is about to happen — joining the last min queue, or an ordinary application.
   */
  isLastMinPhase?: boolean;
}

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

function ModalProvider({ children }: ModalProviderProps) {
  const [modalState, setModalState] = useState<Modals>(Modals.None);
  const [modalOnClick, setModalOnClick] =
    useState<((successful?: boolean, message?: React.ReactElement | string) => void) | undefined>(undefined);
  const [modalBookingData, setModalBookingData] =
    useState<ModalBookingData>({
      bookingInterest: {
        id: 0,
        bookForAnotherPerson: false,
        loggedInUserTicket: {
          id: "",
          firstName: "",
          lastName: "",
          email: "",
          age: 0,
          accessibility: '',
          otherAccessibility: ''
        },
        ticketForAnotherPerson: [],
        error: false,
      },
      minAge: 0,
    });

  const [modalTexts, setModalTexts] =
    useState<ModalTexts>({
      mainBody: ''
    });

  const openModal = (name: Modals, onClick?: (successful?: boolean, message?: React.ReactElement | string) => void) => {
    setModalState(name);
    if (onClick) {
      setModalOnClick(() => onClick);
    }
  };

  const closeModal = () => {
    setModalState(Modals.None);
    setModalOnClick(undefined);
  };

  const setCurrentBookingData = (newModalData: ModalBookingData) => {
    setModalBookingData(newModalData);
  };

  const setCurrentTexts = (newModalTexts: ModalTexts) => {
    setModalTexts(newModalTexts);
  };

  return (
    <ModalContext.Provider value={{ modalState, modalOnClick, openModal, closeModal, modalBookingData, setCurrentBookingData, modalTexts, setCurrentTexts }}>
      {children}
    </ModalContext.Provider>
  );
}

export { ModalProvider };